import { useState } from 'react';
import { Loader2, Search, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function DistTable({ title, data }) {
  const entries = Object.entries(data || {});
  if (!entries.length) return null;
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium">{title}</p>
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left px-3 py-2 font-medium">Value</th>
              <th className="text-right px-3 py-2 font-medium">Count</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([val, count]) => (
              <tr key={val} className="border-b last:border-0">
                <td className="px-3 py-2 font-mono text-xs">{val ?? '(null)'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BookingSampleTable({ title, bookings }) {
  if (!bookings?.length) return null;
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium">{title}</p>
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left px-3 py-2 font-medium">Guest</th>
              <th className="text-left px-3 py-2 font-medium">Check-in</th>
              <th className="text-left px-3 py-2 font-medium">Check-out</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-right px-3 py-2 font-medium">Total Price</th>
              <th className="text-left px-3 py-2 font-medium">Property</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b, i) => (
              <tr key={b.smoobu_id || i} className="border-b last:border-0">
                <td className="px-3 py-2">{b.guest_name}</td>
                <td className="px-3 py-2 font-mono text-xs">{b.check_in}</td>
                <td className="px-3 py-2 font-mono text-xs">{b.check_out}</td>
                <td className="px-3 py-2">
                  <Badge variant={b.status === 'cancelled' ? 'destructive' : 'secondary'}>
                    {b.status}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {b.total_price != null ? Number(b.total_price).toFixed(2) : '(null)'}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{b.property_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecordDump({ title, record }) {
  if (!record) return null;
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium">{title}</p>
      <pre className="overflow-x-auto border rounded-lg p-3 text-xs bg-muted/30 max-h-80 overflow-y-auto">
        {JSON.stringify(record, null, 2)}
      </pre>
    </div>
  );
}

export function SmoobuCancelledAuditPanel() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.smoobuCancelledAudit();
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          Smoobu Cancellation Audit
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Surfaces every candidate cancellation signal on Smoobu reservations:
          status distribution, cancellation-fee counts, zero-price counts,
          notice field values, and the known-cancelled guest record (Aleta Rudrich).
        </p>

        <Button onClick={runAudit} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Run Audit
        </Button>

        {results && (
          <div className="space-y-5">
            {/* Summary counters */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Total bookings</p>
                <p className="text-xl font-bold tabular-nums">{results.total_bookings}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Cancellation fee &gt; 0</p>
                <p className="text-xl font-bold tabular-nums">{results.cancellation_fee_nonzero_count}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Zero/null price</p>
                <p className="text-xl font-bold tabular-nums">{results.zero_or_null_price_count}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Aleta Rudrich found</p>
                <p className="text-xl font-bold tabular-nums">
                  {results.aleta_rudrich_record ? 'Yes' : 'No'}
                </p>
              </div>
            </div>

            {/* Distribution tables */}
            <DistTable title="Status field distribution" data={results.status_distribution} />
            <DistTable title="Ingestion status distribution" data={results.ingestion_status_distribution} />
            <DistTable title="Webhook event distribution" data={results.webhook_event_distribution} />
            {Object.keys(results.notice_values || {}).length > 0 && (
              <DistTable title="Notice field values (non-empty)" data={results.notice_values} />
            )}

            {/* Sample tables */}
            <BookingSampleTable title="Cancelled bookings (up to 5)" bookings={results.cancelled_samples} />
            <BookingSampleTable title="Confirmed bookings (up to 5, for comparison)" bookings={results.confirmed_samples} />

            {/* Aleta Rudrich record dump */}
            <RecordDump title="Aleta Rudrich record (full dump)" record={results.aleta_rudrich_record} />
            {!results.aleta_rudrich_record && (
              <div className="text-sm text-amber-600 dark:text-amber-400">
                No record found matching guest name "Aleta Rudrich". The booking
                may have been deleted or the name may differ from what Smoobu stores.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
